import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { JOURNAL } from "@/constants/testIds";

const OUTCOMES = [
  { key: "good_win", label: "Good Win", color: "border-signal-green text-signal-green" },
  { key: "lucky_win", label: "Lucky Win", color: "border-signal-yellow text-signal-yellow" },
  { key: "good_loss", label: "Good Loss", color: "border-muted-foreground text-muted-foreground" },
  { key: "bad_loss", label: "Bad Loss", color: "border-signal-red text-signal-red" },
  { key: "uncertain", label: "Uncertain", color: "border-border text-foreground" },
];

const CAUSES = [
  "Chased Entry", "Low Volume", "False Breakout", "Resistance",
  "Trend Reversal", "Sector Weakness", "News", "Earnings",
  "Poor Stop", "Poor Target", "Poor Execution", "Manual Override", "Plan Followed",
];

const TAG_SUGGESTIONS = ["BREAKOUT", "MOMENTUM", "PULLBACK", "RSI", "HIGH_VOLUME", "GAP_UP", "OVERSOLD", "REVERSAL"];

export default function PostTradeReviewModal({ open, onClose, journalId, onSubmitted }) {
  const [outcome, setOutcome] = useState("");
  const [primaryCause, setPrimaryCause] = useState("");
  const [secondary, setSecondary] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [notes, setNotes] = useState("");
  const [strategyFollowed, setStrategyFollowed] = useState(true);
  const [criticFollowed, setCriticFollowed] = useState(true);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleSecondary = (c) =>
    setSecondary((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const toggleTag = (t) =>
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const submit = async () => {
    if (!outcome || !primaryCause) {
      toast.error("Outcome and primary cause required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/journal/${journalId}/review`, {
        outcome,
        primary_cause: primaryCause,
        secondary_causes: secondary,
        tags,
        notes,
        strategy_followed: strategyFollowed,
        critic_warning_followed: criticFollowed,
        manual_override: override,
      });
      toast.success("Review saved.");
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid={JOURNAL.reviewModal}
        className="max-w-2xl !rounded-none border-border/70"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-wider text-sm">Post-Trade Review · Required</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <div className="label-cap mb-2">Outcome Classification</div>
            <div className="grid grid-cols-5 gap-2">
              {OUTCOMES.map((o) => (
                <button
                  key={o.key}
                  data-testid={JOURNAL.outcome(o.key)}
                  onClick={() => setOutcome(o.key)}
                  className={`px-2 py-2 text-xs mono uppercase tracking-wider border transition-colors duration-150 ${
                    outcome === o.key
                      ? o.color + " bg-secondary/50"
                      : "border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >{o.label}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-cap mb-2">Primary Root Cause</div>
            <select
              data-testid={JOURNAL.primaryCause}
              className="w-full bg-transparent border border-border/60 h-9 px-2 text-sm"
              value={primaryCause}
              onChange={(e) => setPrimaryCause(e.target.value)}
            >
              <option value="">Select a primary cause...</option>
              {CAUSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <div className="label-cap mb-2">Secondary Causes (optional)</div>
            <div className="flex flex-wrap gap-1.5">
              {CAUSES.map((c) => (
                <button
                  key={c}
                  onClick={() => toggleSecondary(c)}
                  className={`chip !normal-case ${secondary.includes(c) ? "bg-secondary/80 text-foreground" : "text-muted-foreground"}`}
                >{c}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-cap mb-2">Tags</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((t) => (
                <button key={t} className="chip bg-secondary/80" onClick={() => toggleTag(t)}>#{t} ×</button>
              ))}
              {TAG_SUGGESTIONS.filter((t) => !tags.includes(t)).map((t) => (
                <button key={t} className="chip text-muted-foreground" onClick={() => toggleTag(t)}>+ {t}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value.toUpperCase().replace(/\s+/g, "_"))}
                placeholder="Custom tag..."
                className="h-8 !rounded-none"
              />
              <button
                type="button"
                className="btn-outline h-8"
                onClick={() => { if (tagDraft) { setTags([...tags, tagDraft]); setTagDraft(""); } }}
              >Add</button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs">
            <label className="flex items-center gap-2 border border-border/60 p-2 cursor-pointer">
              <input type="checkbox" checked={strategyFollowed} onChange={(e) => setStrategyFollowed(e.target.checked)} />
              Strategy followed
            </label>
            <label className="flex items-center gap-2 border border-border/60 p-2 cursor-pointer">
              <input type="checkbox" checked={criticFollowed} onChange={(e) => setCriticFollowed(e.target.checked)} />
              Critic followed
            </label>
            <label className="flex items-center gap-2 border border-border/60 p-2 cursor-pointer">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
              Manual override
            </label>
          </div>

          <div>
            <div className="label-cap mb-2">Notes</div>
            <Textarea
              data-testid={JOURNAL.notesInput}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="!rounded-none"
              placeholder="What did you learn? What will you change?"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border/50">
            <button className="btn-primary" onClick={submit} disabled={busy} data-testid={JOURNAL.submitBtn}>
              {busy ? "Saving..." : "Submit Review"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
